import React, { useState, useEffect } from "react";
import {
  FaShoppingCart,
  FaTimes,
  FaChevronDown,
  FaSave,
  FaBox,
  FaFilePdf,
  FaTrash,
  FaPlus,
  FaEye,
} from "react-icons/fa";
import * as cartService from "../../services/cart.service";
import { productService } from "../../services/product.service";
import { getProductImageSrc } from "../../components/Cart/cartUtils";
import {
  PDF_EXPORT_CURRENCIES,
  buildCollectionPdfHtmlDocument,
  openPrintWindowForHtml,
} from "../../components/Cart/cartPdfExport";
import CartProductModal from "../../components/Cart/CartProductModal";
import PageHeader from "../../components/PageHeader/PageHeader";
import "./Cart.css";

const Cart = () => {
  const [cartItems, setCartItems] = useState([]);
  const [collections, setCollections] = useState([]);
  const [userCollections, setUserCollections] = useState([]);
  const [selectedCollection, setSelectedCollection] = useState(null);
  const [showCollectionDropdown, setShowCollectionDropdown] = useState(false);
  const [selectedAction, setSelectedAction] = useState("");
  const [showActionPanel, setShowActionPanel] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [modalSpecsProduct, setModalSpecsProduct] = useState(null);
  const [modalSpecsLoading, setModalSpecsLoading] = useState(false);
  const [showProductModal, setShowProductModal] = useState(false);
  /** Currency used only for PDF export price column */
  const [pdfExportCurrency, setPdfExportCurrency] = useState("INR");
  const [showPdfFieldsDropdown, setShowPdfFieldsDropdown] = useState(false);
  const [pdfExportFields, setPdfExportFields] = useState([
    'SKU#',
    'DESCRIPTION',
    'CATEGORY',
    'DIMENSIONS',
  ]);

  const PDF_EXPORT_FIELDS = [
    'SKU#',
    'DESCRIPTION',
    'DIMENSIONS',
    'FINISH',
    'MATERIAL',
    'COLOR',
    'NET WEIGHT',
    'PRICE',
    'HSN',
    'CATEGORY',
    'MOQ',
    'INNER PACK',
    'MASTER PACK',
    'REMARKS',
  ];

  // Form states for Create Collection
  const [createCollectionForm, setCreateCollectionForm] = useState({
    name: "",
    remark: "",
  });

  // Form states for Update Collection
  const [updateCollectionForm, setUpdateCollectionForm] = useState({
    collectionId: "",
  });

  // Load cart items and collections on mount
  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setIsLoading(true);
    try {
      // Load cart items
      const cartData = await cartService.getShoppingCart();
      setCartItems(cartData);

      // Load user collections
      const collectionsData = await cartService.getUserCollections();
      setUserCollections(collectionsData);

      // Extract unique collections from user collections for backward compatibility
      const uniqueCollections = [];
      const collectionIds = new Set();
      collectionsData.forEach((collection) => {
        if (!collectionIds.has(collection.id)) {
          collectionIds.add(collection.id);
          uniqueCollections.push({
            id: collection.id,
            name: collection.name,
          });
        }
      });
      setCollections(uniqueCollections);
    } catch (error) {
      console.error("Error loading data:", error);
      setMessage("Failed to load cart data");
    } finally {
      setIsLoading(false);
    }
  };

  const loadUserCollections = async () => {
    try {
      const userCollections = await cartService.getUserCollections();
      setUserCollections(userCollections);
    } catch (error) {
      console.error("Error loading user collections:", error);
    }
  };

  const removeFromCart = async (productUid) => {
    try {
      setIsLoading(true);
      await cartService.removeFromCart(productUid);
      const updated = cartItems.filter((item) => item.uid !== productUid);
      setCartItems(updated);
      setMessage("Item removed from cart");
    } catch (error) {
      console.error("Error removing from cart:", error);
      setMessage("Failed to remove item from cart");
    } finally {
      setIsLoading(false);
    }
  };

  const clearCart = async () => {
    try {
      setIsLoading(true);
      await cartService.clearShoppingCart();
      setCartItems([]);
      setMessage("Cart cleared successfully");
    } catch (error) {
      console.error("Error clearing cart:", error);
      setMessage("Failed to clear cart");
    } finally {
      setIsLoading(false);
    }
  };

  const generateCollectionPDF = async () => {
    if (!selectedCollection) {
      setMessage("Please select a collection first");
      return;
    }

    if (pdfExportFields.length === 0) {
      setMessage("Please select at least one PDF field.");
      return;
    }

    const products = selectedCollection.products || [];
    if (products.length === 0) {
      setMessage("This collection has no products to export.");
      return;
    }

    const currencyMeta =
      PDF_EXPORT_CURRENCIES.find((c) => c.code === pdfExportCurrency) ||
      PDF_EXPORT_CURRENCIES[0];
    const currencySymbol = currencyMeta.symbol;
    const currencyCode = currencyMeta.code;

    setIsLoading(true);
    try {
      const enrichedProducts = await Promise.all(
        products.map(async (p) => {
          const uid = p?.uid;
          if (!uid) return p;
          try {
            const specs = await productService.getProductSpecsByUid(
              String(uid)
            );
            if (specs && typeof specs === "object") {
              return { ...p, ...specs };
            }
          } catch (e) {
            console.warn("PDF: could not load catalog product", uid, e);
          }
          return p;
        })
      );

      const htmlContent = buildCollectionPdfHtmlDocument(
        selectedCollection.name,
        enrichedProducts,
        currencySymbol,
        currencyCode,
        pdfExportFields,
      );

      const ok = openPrintWindowForHtml(htmlContent);
      if (!ok) {
        alert("Please allow pop-ups to generate the PDF.");
      }
    } catch (error) {
      console.error("Error generating PDF:", error);
      alert("Error generating PDF");
    } finally {
      setIsLoading(false);
    }
  };

  // Handle Create Collection
  const handleCreateCollection = async () => {
    if (!createCollectionForm.name.trim()) {
      setMessage("Please enter collection name");
      return;
    }

    if (cartItems.length === 0) {
      setMessage("Please add items to cart first");
      return;
    }

    try {
      setIsLoading(true);
      setMessage(
        `Collection "${createCollectionForm.name}" created successfully with ${cartItems.length} items!`
      );

      // Reset form
      setCreateCollectionForm({ name: "", remark: "" });
      setSelectedAction("");
      setShowActionPanel(false);
      await clearCart();
      
      // Reload user collections
      await loadUserCollections();
    } catch (error) {
      console.error("Error creating collection:", error);
      setMessage("Error creating collection");
    } finally {
      setIsLoading(false);
    }
  };

  // Handle Update Collection
  const handleUpdateCollection = async () => {
    if (!updateCollectionForm.collectionId) {
      setMessage("Please select a collection");
      return;
    }

    if (cartItems.length === 0) {
      setMessage("Please add items to cart first");
      return;
    }

    try {
      setIsLoading(true);
      const collection = userCollections.find(
        (c) => c.id === Number(updateCollectionForm.collectionId)
      );

      setMessage(
        `Added ${cartItems.length} product(s) to collection "${collection?.name}"!`
      );

      // Reset form
      setUpdateCollectionForm({ collectionId: "" });
      setSelectedAction("");
      setShowActionPanel(false);
      await clearCart();
      
      // Reload user collections
      await loadUserCollections();
    } catch (error) {
      console.error("Error updating collection:", error);
      setMessage("Error updating collection");
    } finally {
      setIsLoading(false);
    }
  };

  // Remove product from collection (uses API response so state matches server)
  const handleRemoveProductFromCollection = async (productUid) => {
    if (!selectedCollection) return;

    const confirmDelete = window.confirm(
      "Are you sure you want to delete this product from the collection?"
    );

    if (!confirmDelete) return;

    try {
      setIsLoading(true);
      const updatedCollection = await cartService.deleteProductFromCollection(
        selectedCollection.id,
        productUid
      );

      setSelectedCollection(updatedCollection);
      setUserCollections((prev) =>
        prev.map((c) =>
          c.id === updatedCollection.id
            ? { ...c, products: updatedCollection.products }
            : c
        )
      );

      if (selectedProduct?.uid === productUid) {
        setSelectedProduct(null);
        setModalSpecsProduct(null);
        setShowProductModal(false);
      }

      setMessage("Product removed from collection");
    } catch (error) {
      console.error("Error deleting product:", error);
      setMessage("Failed to remove product from collection");
    } finally {
      setIsLoading(false);
    }
  };

  // Add current cart items to selected collection
  const addProductsToSelectedCollection = async () => {
    if (!selectedCollection) {
      setMessage("Please select a collection first");
      return;
    }

    if (cartItems.length === 0) {
      setMessage("Cart is empty. Add products first");
      return;
    }

    const confirmAdd = window.confirm(
      `Add ${cartItems.length} product(s) to "${selectedCollection.name}"?`
    );

    if (!confirmAdd) return;

    try {
      setIsLoading(true);
      const updatedCollection = await cartService.addProductsToCollection(
        selectedCollection.id,
        cartItems
      );

      setSelectedCollection(updatedCollection);
      setMessage(
        `Added ${cartItems.length} product(s) to "${selectedCollection.name}"!`
      );

      // Clear cart
      await clearCart();
      
      // Reload collections
      await loadUserCollections();
    } catch (error) {
      console.error("Error adding products:", error);
      setMessage("Failed to add products to collection");
    } finally {
      setIsLoading(false);
    }
  };

  // Show product details — merge collection snapshot with live catalog specs when available
  const showProductDetails = async (product) => {
    setSelectedProduct(product);
    setModalSpecsProduct(null);
    setShowProductModal(true);

    const uid = product?.uid;
    if (!uid) return;

    setModalSpecsLoading(true);
    try {
      const specs = await productService.getProductSpecsByUid(uid);
      if (specs) setModalSpecsProduct(specs);
    } catch (err) {
      console.error("Could not load product specifications:", err);
    } finally {
      setModalSpecsLoading(false);
    }
  };

  const renderActionPanel = () => {
    if (!selectedAction) return null;

    switch (selectedAction) {
      case "create-collection":
        return (
          <div className="action-panel">
            <h5>Create New Collection</h5>
            <div className="form-group mb-3">
              <label className="form-label">Collection Name *</label>
              <input
                type="text"
                className="form-control"
                placeholder="Enter collection name"
                value={createCollectionForm.name}
                onChange={(e) =>
                  setCreateCollectionForm({
                    ...createCollectionForm,
                    name: e.target.value,
                  })
                }
              />
            </div>
            <div className="form-group mb-3">
              <label className="form-label">Remark</label>
              <textarea
                className="form-control"
                rows="3"
                placeholder="Add any remarks..."
                value={createCollectionForm.remark}
                onChange={(e) =>
                  setCreateCollectionForm({
                    ...createCollectionForm,
                    remark: e.target.value,
                  })
                }
              />
            </div>
            <button
              className="btn btn-success"
              onClick={handleCreateCollection}
              disabled={isLoading}
            >
              <FaSave className="me-2" /> Save Collection
            </button>
          </div>
        );

      case "update-collection":
        return (
          <div className="action-panel">
            <h5>Update Existing Collection</h5>
            <div className="form-group mb-3">
              <label className="form-label">Select Collection *</label>
              <select
                className="form-control"
                value={updateCollectionForm.collectionId}
                onChange={(e) =>
                  setUpdateCollectionForm({
                    ...updateCollectionForm,
                    collectionId: e.target.value,
                  })
                }
              >
                <option value="">-- Select a collection --</option>
                {userCollections.map((collection) => (
                  <option key={collection.id} value={collection.id}>
                    {collection.name} ({collection.products.length} items)
                  </option>
                ))}
              </select>
            </div>
            <button
              className="btn btn-success"
              onClick={handleUpdateCollection}
              disabled={isLoading}
            >
              <FaSave className="me-2" /> Add to Collection
            </button>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="cart-container">
      {/* Header */}
      <PageHeader
        icon={<FaShoppingCart size={22} />}
        title="Shopping Cart"
        subtitle={`${cartItems.length} item${cartItems.length !== 1 ? "s" : ""} in cart`}
      />

      {/* Loading overlay while any cart operation is in progress */}
      {isLoading && (
        <div className="cart-loading-overlay" role="status" aria-live="polite">
          <div className="cart-spinner" aria-hidden="true" />
          <span className="cart-loading-text">Working…</span>
        </div>
      )}

      {/* Message Display */}
      {message && (
        <div
          className="alert alert-info cart-inline-alert"
          role="alert"
        >
          <span>{message}</span>
          <button
            type="button"
            className="btn-close"
            onClick={() => setMessage("")}
            aria-label="Close"
          />
        </div>
      )}

      {/* Main Content */}
      <div className="cart-main">
        <div className="cart-content">
          
          {/* Existing Collections Section */}
          {userCollections.length > 0 && (
            <div className="collections-section mb-4">
              <h4 className="section-title">Existing Collections</h4>
              <div className="collection-selector">
                <div className="dropdown-wrapper">
                  <button
                    className="btn btn-outline-primary collection-dropdown-btn"
                    onClick={() => setShowCollectionDropdown(!showCollectionDropdown)}
                  >
                    <span>
                      {selectedCollection
                        ? selectedCollection.name
                        : "Select a Collection"}
                    </span>
                    <FaChevronDown
                      className={`dropdown-icon ${
                        showCollectionDropdown ? "open" : ""
                      }`}
                    />
                  </button>
                  {showCollectionDropdown && (
                    <div className="collection-dropdown-menu show">
                      {userCollections.map((collection) => (
                        <button
                          key={collection.id}
                          className={`collection-dropdown-item ${
                            selectedCollection?.id === collection.id
                              ? "active"
                              : ""
                          }`}
                          onClick={async () => {
                            try {
                              setIsLoading(true);
                              const collectionData = await cartService.getCollection(collection.id);
                              setSelectedCollection(collectionData);
                              setShowCollectionDropdown(false);
                            } catch (error) {
                              console.error("Error loading collection:", error);
                              setMessage("Failed to load collection details");
                            } finally {
                              setIsLoading(false);
                            }
                          }}
                        >
                          <div className="collection-name">{collection.name}</div>
                          <div className="collection-meta">
                            {collection.products.length} items
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Selected Collection View */}
              {selectedCollection && (
                <div className="selected-collection">
                  <div className="collection-header">
                    <div className="collection-info">
                      <h5>{selectedCollection.name}</h5>
                      <p className="text-muted mb-1">
                        {selectedCollection.products.length} product
                        {selectedCollection.products.length !== 1 ? "s" : ""}
                      </p>
                      {selectedCollection.remark && (
                        <p className="text-muted mb-0">
                          <strong>Remark:</strong> {selectedCollection.remark}
                        </p>
                      )}
                    </div>
                    <div className="d-flex align-items-center gap-2 flex-wrap justify-content-end">
                      <label
                        htmlFor="pdf-export-fields"
                        className="small text-muted mb-0 text-nowrap"
                      >
                        Show Details for:
                      </label>
                      <div className="dropdown-wrapper">
                        <button
                          type="button"
                          className="btn btn-outline-secondary btn-sm dropdown-btn pdf-fields-dropdown-btn"
                          aria-haspopup="true"
                          aria-expanded={showPdfFieldsDropdown}
                          onClick={() => setShowPdfFieldsDropdown((prev) => !prev)}
                        >
                          <span>
                            {pdfExportFields.length} field{pdfExportFields.length !== 1 ? 's' : ''}
                          </span>
                          <FaChevronDown
                            className={`dropdown-icon ${showPdfFieldsDropdown ? 'open' : ''}`}
                          />
                        </button>
                        {showPdfFieldsDropdown && (
                          <div className="dropdown-menu show" style={{ maxHeight: '300px', overflowY: 'auto' }}>
                            {PDF_EXPORT_FIELDS.map((field) => (
                              <label
                                key={field}
                                className="dropdown-item d-flex align-items-center"
                                style={{ cursor: 'pointer' }}
                              >
                                <input
                                  type="checkbox"
                                  className="form-check-input me-2"
                                  checked={pdfExportFields.includes(field)}
                                  onChange={() => {
                                    setPdfExportFields((prev) =>
                                      prev.includes(field)
                                        ? prev.filter((item) => item !== field)
                                        : [...prev, field],
                                    );
                                  }}
                                />
                                {field}
                              </label>
                            ))}
                          </div>
                        )}
                      </div>
                      <label
                        htmlFor="pdf-export-currency"
                        className="small text-muted mb-0 text-nowrap"
                      >
                        PDF currency
                      </label>
                      <select
                        id="pdf-export-currency"
                        className="form-select form-select-sm cart-pdf-currency-select"
                        value={pdfExportCurrency}
                        onChange={(e) => setPdfExportCurrency(e.target.value)}
                      >
                        {PDF_EXPORT_CURRENCIES.map((c) => (
                          <option key={c.code} value={c.code}>
                            {c.label}
                          </option>
                        ))}
                      </select>
                      <button
                        type="button"
                        className="btn btn-primary"
                        onClick={generateCollectionPDF}
                        disabled={isLoading}
                      >
                        <FaFilePdf className="me-2" /> Generate PDF
                      </button>
                    </div>
                  </div>

                  {/* Collection Products Grid */}
                  <div className="collection-products-grid">
                    {selectedCollection.products.map((product) => {
                      const imageSrc =
                        getProductImageSrc(product) || product.mainImage || null;

                      return (
                        <div
                          key={product.uid}
                          className="collection-product-card"
                        >
                          <div className="product-image-container">
                            {imageSrc ? (
                              <img
                                src={imageSrc}
                                alt={product.itemNo}
                                className="product-image collection-product-img"
                              />
                            ) : (
                              <div className="product-image-placeholder collection-product-placeholder-inner">
                                <FaBox size={40} className="text-muted" />
                              </div>
                            )}

                            <div className="collection-product-overlay">
                              <button
                                type="button"
                                className="collection-overlay-btn collection-overlay-btn--view"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  showProductDetails(product);
                                }}
                                title="View Details"
                                aria-label={`View details for ${product.itemNo || product.uid || "product"}`}
                              >
                                <FaEye size={12} aria-hidden /> View
                              </button>
                              <button
                                type="button"
                                className="collection-overlay-btn collection-overlay-btn--delete"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleRemoveProductFromCollection(product.uid);
                                }}
                                title="Delete"
                                aria-label={`Remove ${product.itemNo || product.uid || "product"} from collection`}
                              >
                                <FaTrash size={12} /> Delete
                              </button>
                            </div>
                          </div>
                          <div className="product-info collection-product-info-padded">
                            <div className="product-item-no">
                              <strong>Item:</strong>
                              <span>{product.itemNo || "N/A"}</span>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Add Products to Collection Button */}
                  {cartItems.length > 0 && (
                    <div className="cart-add-to-collection-bar">
                      <button
                        className="btn btn-primary"
                        onClick={addProductsToSelectedCollection}
                        disabled={isLoading}
                      >
                        <FaPlus className="me-2" /> Add {cartItems.length} Product
                        {cartItems.length > 1 ? "s" : ""} to Collection
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {cartItems.length === 0 ? (
            <div className="empty-cart">
              <FaShoppingCart size={48} className="text-muted mb-3" />
              <h4>Your cart is empty</h4>
              <p className="text-muted">
                Add products from the catalog to get started
              </p>
              <a href="/dashboard/product/catalog" className="btn btn-primary">
                Browse Catalog
              </a>
            </div>
          ) : (
            <div className="cart-items-section">
              {/* Cart Items Grid */}
              <div className="cart-items-grid">
                {cartItems.map((product) => {
                  // Handle both old format (isMain + data) and new format (path)
                  let imageSrc = null;
                  
                  if (product.images && Array.isArray(product.images)) {
                    // Try to find image with isMain property (old format)
                    const mainImage = product.images.find((img) => img.isMain);
                    if (mainImage && mainImage.data) {
                      imageSrc = mainImage.data;
                    } 
                    // Otherwise use first image path (new format from API)
                    else if (product.images[0]?.path) {
                      imageSrc = product.images[0].path;
                    }
                  }

                  return (
                    <div key={product.uid} className="cart-item-card">
                      {/* Product Image */}
                      <div className="product-image-container">
                        {imageSrc ? (
                          <img
                            src={imageSrc}
                            alt={product.itemNo}
                            className="product-image"
                          />
                        ) : (
                          <div className="product-image-placeholder">
                            <FaBox size={40} className="text-muted" />
                          </div>
                        )}
                      </div>

                      {/* Product Info */}
                      <div className="product-info">
                        <div className="product-item-no">
                          <strong>Item:</strong>
                          <span>{product.itemNo || "N/A"}</span>
                        </div>
                        <p className="product-uid text-muted small d-flex align-items-center gap-2">
                          <strong>{product?.productName || "N/A"}</strong>
                          {product.color && product.color.length > 0 && (
                            <p className="product-specs">
                              <span className="badge bg-secondary">
                                {product.color.length} color
                                {product.color.length > 1 ? "s" : ""}
                              </span>
                            </p>
                          )}
                        </p>
                      </div>

                      {/* Remove Button */}
                      <button
                        className="btn-remove-item"
                        onClick={() => removeFromCart(product.uid)}
                        title="Remove from cart"
                      >
                        <FaTimes />
                      </button>
                    </div>
                  );
                })}
              </div>

              {/* Action Dropdown & Panel */}
              <div className="cart-actions-section">
                <div className="action-dropdown">
                  <label className="action-label">Action:</label>
                  <div className="dropdown-wrapper">
                    <button
                      className="btn btn-outline-primary dropdown-btn"
                      onClick={() => setShowActionPanel(!showActionPanel)}
                    >
                      <span>
                        {selectedAction
                          ? selectedAction
                              .split("-")
                              .map(
                                (word) =>
                                  word.charAt(0).toUpperCase() + word.slice(1)
                              )
                              .join(" ")
                          : "Select Action"}
                      </span>
                      <FaChevronDown
                        className={`dropdown-icon ${
                          showActionPanel ? "open" : ""
                        }`}
                      />
                    </button>
                    {showActionPanel && (
                      <div className="dropdown-menu show">
                        <button
                          className={`dropdown-item ${
                            selectedAction === "create-collection"
                              ? "active"
                              : ""
                          }`}
                          onClick={() => {
                            setSelectedAction("create-collection");
                            setShowActionPanel(false);
                          }}
                        >
                          Create Collection
                        </button>
                        {collections.length > 0 && (
                          <button
                            className={`dropdown-item ${
                              selectedAction === "update-collection"
                                ? "active"
                                : ""
                            }`}
                            onClick={() => {
                              setSelectedAction("update-collection");
                              setShowActionPanel(false);
                            }}
                          >
                            Update Collection
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {/* Action Panel */}
                {renderActionPanel()}

                {/* Clear Cart Button */}
                <button
                  className="btn btn-outline-danger mt-3"
                  onClick={clearCart}
                  disabled={isLoading}
                >
                  <FaTimes className="me-2" /> Clear Cart
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Product Detail Modal */}
      <CartProductModal
        product={
          selectedProduct && modalSpecsProduct
            ? { ...selectedProduct, ...modalSpecsProduct }
            : selectedProduct
        }
        show={showProductModal}
        specsLoading={modalSpecsLoading}
        onClose={() => {
          setShowProductModal(false);
          setModalSpecsProduct(null);
        }}
      />
    </div>
  );
};

export default Cart;
